import React, { useRef, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import confetti from "canvas-confetti";

const futura = "'Futura', sans-serif";
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

const Newsletter = () => {
  const [mode, setMode] = useState("subscribe"); // "subscribe" | "unsubscribe"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const cardRef = useRef(null);

  const fireConfetti = () => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    confetti({
      particleCount: 100,
      spread: 70,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      },
    });
  };

  const setMsg = (text, error = false) => {
    setMessage(text);
    setIsError(error);
  };

  const switchMode = (next) => {
    setMode(next);
    setMsg("");
  };

  const handleSubscribe = async () => {
    if (!email) return setMsg("Please enter an email address.", true);
    if (!emailRegex.test(email))
      return setMsg("Please enter a valid email address.", true);

    setLoading(true);
    setMsg("");
    try {
      // Try saving with the name; fall back to email-only if the table
      // doesn't have a name column so signup never silently fails.
      const post = (body) =>
        fetch(`${SUPABASE_URL}/rest/v1/email_list`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${SUPABASE_KEY}`,
            Prefer: "return=representation",
          },
          body: JSON.stringify(body),
        });

      let response = await post(
        name.trim() ? { email, name: name.trim() } : { email }
      );
      if (!response.ok && name.trim()) {
        response = await post({ email });
      }

      if (response.ok) {
        setMsg("You're in! Thanks for subscribing.");
        setName("");
        setEmail("");
        fireConfetti();
        if (typeof window.gtag === "function") {
          window.gtag("event", "sign_up", { method: "newsletter" });
        }
      } else {
        setMsg(
          "There was an issue signing you up. Please try again later.",
          true
        );
      }
    } catch (err) {
      setMsg("There was an error. Please try again later.", true);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!email) return setMsg("Please enter an email address.", true);
    if (!emailRegex.test(email))
      return setMsg("Please enter a valid email address.", true);

    setLoading(true);
    setMsg("");
    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/email_list?email=eq.${encodeURIComponent(
          email
        )}`,
        {
          method: "DELETE",
          headers: {
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${SUPABASE_KEY}`,
            Prefer: "return=representation",
          },
        }
      );

      if (response.ok) {
        const removed = await response.json().catch(() => []);
        if (Array.isArray(removed) && removed.length === 0) {
          setMsg("That email isn't on our list.", true);
        } else {
          setMsg("You've been unsubscribed. Sorry to see you go.");
          setEmail("");
        }
      } else {
        setMsg(
          "There was an issue unsubscribing you. Please try again later.",
          true
        );
      }
    } catch (err) {
      setMsg("There was an error. Please try again later.", true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      mode === "subscribe" ? handleSubscribe() : handleUnsubscribe();
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "10px",
    border: "1px solid #333",
    background: "#141414",
    color: "white",
    fontFamily: futura,
    fontSize: "15px",
    boxSizing: "border-box",
    outline: "none",
  };

  return (
    <div style={{ background: "#0c0c0c", minHeight: "100vh" }}>
      <SEO
        title="Newsletter - Stay in the Loop"
        description="Subscribe to The Brik newsletter for exclusive updates, early access, and deals on the metal wallet. Unsubscribe anytime."
        path="/newsletter"
        keywords="the brik newsletter, brik wallet updates, subscribe, unsubscribe"
      />
      <Header />

      <section
        style={{
          background:
            "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "80px 24px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          ref={cardRef}
          style={{
            width: "100%",
            maxWidth: "480px",
            background: "#161616",
            border: "1px solid #262626",
            borderRadius: "18px",
            padding: "40px 32px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
          }}
        >
          <span
            style={{
              display: "inline-block",
              background: "#ff7d1a",
              color: "white",
              padding: "5px 12px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              fontFamily: futura,
              marginBottom: "18px",
            }}
          >
            {mode === "subscribe" ? "Join the list" : "Manage subscription"}
          </span>

          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 5vw, 42px)",
              lineHeight: 1.05,
              letterSpacing: "-1px",
              margin: "0 0 12px",
            }}
          >
            {mode === "subscribe"
              ? "Stay in the Loop."
              : "Unsubscribe."}
          </h1>

          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.5,
              color: "#bbb",
              margin: "0 0 28px",
            }}
          >
            {mode === "subscribe"
              ? "Get exclusive updates, early access, and deals on The Brik. No spam, just the good stuff."
              : "Enter your email below to be removed from our list. You can resubscribe anytime."}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {mode === "subscribe" && (
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyPress={handleKeyPress}
                style={inputStyle}
              />
            )}
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              style={inputStyle}
            />

            <button
              onClick={mode === "subscribe" ? handleSubscribe : handleUnsubscribe}
              disabled={loading}
              style={{
                width: "100%",
                padding: "16px",
                background: mode === "subscribe" ? "#ff7d1a" : "#b71c1c",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "15px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                letterSpacing: "1px",
                fontFamily: futura,
                textTransform: "uppercase",
              }}
            >
              {loading
                ? "Working…"
                : mode === "subscribe"
                ? "Subscribe"
                : "Unsubscribe"}
            </button>
          </div>

          {message && (
            <p
              style={{
                margin: "18px 0 0",
                fontFamily: futura,
                fontSize: "14px",
                fontWeight: 600,
                color: isError ? "#ff8a80" : "#7fffa8",
              }}
            >
              {message}
            </p>
          )}

          <p
            style={{
              margin: "28px 0 0",
              fontFamily: futura,
              fontSize: "13px",
              color: "#888",
              textAlign: "center",
            }}
          >
            {mode === "subscribe" ? (
              <>
                Already subscribed?{" "}
                <button
                  onClick={() => switchMode("unsubscribe")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#ff7d1a",
                    cursor: "pointer",
                    fontFamily: futura,
                    fontSize: "13px",
                    fontWeight: 700,
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Unsubscribe here
                </button>
              </>
            ) : (
              <>
                Changed your mind?{" "}
                <button
                  onClick={() => switchMode("subscribe")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#ff7d1a",
                    cursor: "pointer",
                    fontFamily: futura,
                    fontSize: "13px",
                    fontWeight: 700,
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Subscribe instead
                </button>
              </>
            )}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Newsletter;
